// Finished capture work keyed by the live source markup. A layer that mounts
// again unchanged (returning to a page, undo, a duplicate shape) reuses the
// snapshot, frame masks and prepared filter images instead of rendering them
// again. Any edit changes the markup, which is a different key.
import { DISPLAY_MASK_MOUNT_ID } from '../../masks/constants';
import {
  CSS_VARIABLE_PREFIX,
  SAVED_COLORS_STYLE_TAG_ID,
} from '../../app/savedColors/constants';

const LIMIT = 50 * 1024 * 1024;

type Entry = { promise?: Promise<unknown>; value?: unknown; size: number };
// One least-recently-used list for pending work, finished work and published
// results, so the cap covers everything the cache keeps alive.
const entries = new Map<string, Entry>();
let bytes = 0;

// Glyphs can change without a markup change when a font face arrives later.
let fontEpoch = 0;
try {
  document.fonts?.addEventListener('loadingdone', () => {
    fontEpoch++;
  });
} catch {
  // Captures that depend on fonts simply never reuse without the event.
}
export const getLinearLightFontEpoch = () => fontEpoch;

// cyrb53: a fast 53-bit string hash, so keys stay short for ~70 KB markup.
function digest(text: string) {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

// Undefined means the layer must not be cached: media frames and animation
// positions are not part of the markup, so equal markup is not equal paint.
// Per-mount mask ids must not separate otherwise identical layers.
export function getLinearLightSourceSignature(
  source: HTMLElement,
  context: Record<string, unknown> = {}
): string | undefined {
  if (
    source.querySelector('video,canvas') ||
    source.getAnimations?.({ subtree: true }).length
  )
    return undefined;
  const markup = source.innerHTML;
  return digest(
    [
      markup.replace(DISPLAY_MASK_MOUNT_ID, '-display$1-#'),
      // Saved colours paint through root variables: editing one changes the
      // paint but not the markup that references it.
      markup.includes(CSS_VARIABLE_PREFIX)
        ? document.getElementById(SAVED_COLORS_STYLE_TAG_ID)?.textContent
        : '',
      source.offsetWidth,
      source.offsetHeight,
      window.devicePixelRatio || 1,
      JSON.stringify(context),
    ].join('|')
  );
}

function sizeOf(value: unknown, depth = 0): number {
  if (typeof value === 'string') return value.length;
  if (value && typeof value === 'object' && depth < 3)
    return Object.values(value as Record<string, unknown>).reduce<number>(
      (total, field) => total + sizeOf(field, depth + 1),
      0
    );
  return 0;
}

function touch(key: string, entry: Entry) {
  // Most recently used entries survive eviction longest.
  entries.delete(key);
  entries.set(key, entry);
}

function evict() {
  for (const [key, entry] of Array.from(entries)) {
    if (bytes <= LIMIT) break;
    // Pending work has no size yet; it is accounted for when it settles.
    if (!entry.size) continue;
    bytes -= entry.size;
    entries.delete(key);
  }
}

function store(key: string, entry: Entry) {
  const previous = entries.get(key);
  if (previous) bytes -= previous.size;
  touch(key, entry);
  bytes += entry.size;
  evict();
}

export function reuseLinearLightWork<T>(
  key: string | undefined,
  run: () => Promise<T>
): Promise<T> {
  if (!key) return run();
  const cached = entries.get(key);
  if (cached?.promise) {
    touch(key, cached);
    return cached.promise as Promise<T>;
  }
  const promise = run();
  const entry: Entry = { promise, size: 0 };
  store(key, entry);
  promise.then(
    (value) => {
      // Evicted or replaced while pending: nothing to account for.
      if (entries.get(key) !== entry) return;
      entry.size = key.length + sizeOf(value);
      bytes += entry.size;
      evict();
    },
    () => {
      if (entries.get(key) === entry) entries.delete(key);
    }
  );
  return promise;
}

// The complete published capture for a signature, for a synchronous first
// paint when the same layer mounts again.
export function storeLinearLightResult<T>(key: string | undefined, value: T) {
  if (key) store(key, { value, size: key.length + sizeOf(value) });
}

export function getLinearLightResult<T>(
  key: string | undefined
): T | undefined {
  const entry = key ? entries.get(key) : undefined;
  if (!key || !entry || entry.value === undefined) return undefined;
  touch(key, entry);
  return entry.value as T;
}

export function hasLinearLightResults() {
  return entries.size > 0;
}

export function getLinearLightCacheBytes() {
  return bytes;
}

export function clearLinearLightCache() {
  entries.clear();
  bytes = 0;
}
